import { useTranslation } from "react-i18next"

import { useMutation as useApolloMutation } from "@apollo/client"

import { callMutation } from "shared/api/callMutation"
import { MutationSchema } from "shared/api/types"
import { useEvent } from "shared/lib/useEvent"
import { useRequestToasts } from "shared/lib/useRequestToasts"
import { UpdateFollowUpConfigurationInput } from "shared/types/__generated__/graphql"
import {
  FollowUpConfigurationSchema,
  UpdateFollowUpConfigurationInputSchema
} from "shared/types/__generated__/validation"

import z from "zod"

import {
  UpdateFollowUpConfigurationDocument,
  UpdateFollowUpConfigurationMutation,
  UpdateFollowUpConfigurationMutationVariables
} from "../graphql/mutations.generated"
import { FollowUpConfigurationsDocument } from "../graphql/queries.generated"

const UpdateFollowUpConfigurationVariablesSchema = z.object({
  id: z.string(),
  input: UpdateFollowUpConfigurationInputSchema()
})
const UpdateFollowUpConfigurationResponseSchema = MutationSchema.extend({
  followUpConfiguration: FollowUpConfigurationSchema()
})

export function useUpdateFollowUpConfiguration() {
  const { t } = useTranslation()
  const [apolloMutate, rest] = useApolloMutation<
    UpdateFollowUpConfigurationMutation,
    UpdateFollowUpConfigurationMutationVariables
  >(UpdateFollowUpConfigurationDocument)

  const { raiseLoadingToast } = useRequestToasts({
    messages: {
      loading: t(
        "Request.FollowUpConfiguration.UPDATE_FOLLOW_UP_CONFIGURATION.Start"
      ),
      success: t(
        "Request.FollowUpConfiguration.UPDATE_FOLLOW_UP_CONFIGURATION.Success"
      ),
      failure: t(
        "Request.FollowUpConfiguration.UPDATE_FOLLOW_UP_CONFIGURATION.Failure"
      )
    }
  })

  const updateFollowUpConfiguration = useEvent(
    (id: string, input: UpdateFollowUpConfigurationInput) => {
      const { raiseSuccessToast, raiseErrorToast } = raiseLoadingToast()

      return callMutation(apolloMutate, {
        variables: { id, input },
        transform: data => data.followUpConfiguration,
        schemas: {
          input: UpdateFollowUpConfigurationVariablesSchema,
          response: UpdateFollowUpConfigurationResponseSchema
        },
        apolloOptions: {
          refetchQueries: [{ query: FollowUpConfigurationsDocument }],
          awaitRefetchQueries: true,
          onCompleted: raiseSuccessToast,
          onError: raiseErrorToast
        }
      })
    }
  )

  return { updateFollowUpConfiguration, ...rest }
}
